import { WithPos } from "@customTypes/generics";
import { LawsuitAppealIncident, LawsuitInfo } from "@customTypes/responses/lawsuit";
import { isAppealIncident } from "@customTypes/typeGuards";

export const compareInstanceAndDates = (a: LawsuitAppealIncident, b: LawsuitAppealIncident) =>
  a.lawsuit?.instanceNumber - b.lawsuit?.instanceNumber || a.createdAt - b.createdAt || a.id - b.id;

export const comparePosY = (
  a: WithPos<LawsuitAppealIncident>,
  b: WithPos<LawsuitAppealIncident>
) => (a.pos.y > b.pos.y ? 1 : -1);

export const compareOrder = (
  a: WithPos<LawsuitAppealIncident>,
  b: WithPos<LawsuitAppealIncident>
) => (a.order > b.order ? 1 : -1);

export const filterChildren = ({ parentCaseId }: { parentCaseId: number }, parentId: number) =>
  parentCaseId === parentId;

export const addAppealsIncidentsPos = (
  movements: { appeals: LawsuitAppealIncident[]; incidents: LawsuitAppealIncident[] },
  rootCaseId: number
) => {
  let { appeals, incidents } = movements;
  let order = 0;
  const mapIncidentChildren = (
    { id }: { id: number },
    pos: { x: number; y: number; z: number }
  ) => {
    order += 1;
    incidents = incidents.map((i) => (i.id === id ? { ...i, pos, order } : i));
    incidents
      .sort(compareInstanceAndDates)
      .filter(({ parentCaseId }) => parentCaseId === id)
      .forEach((child, index) => mapIncidentChildren(child, { x: pos.x + 1, y: index, z: pos.z }));
  };
  incidents
    .sort(compareInstanceAndDates)
    .filter(({ parentCaseId }) => parentCaseId === rootCaseId)
    .forEach((incident, index) => mapIncidentChildren(incident, { x: 1, y: index, z: 0 }));
  appeals.sort(compareInstanceAndDates).forEach(({ id }, index) => {
    order += 1;
    appeals = appeals.map((a) =>
      a.id === id
        ? {
            ...a,
            pos: {
              x: 0,
              y: index + 1,
              z: index + 1,
            },
            order,
          }
        : a
    );
    incidents
      .filter(({ parentCaseId }) => parentCaseId === id)
      .forEach((incident, childIndex) =>
        mapIncidentChildren(incident, { x: 1, y: childIndex, z: index + 1 })
      );
  });

  const checkPos = (e: WithPos<LawsuitAppealIncident>) =>
    typeof e.pos === "object" &&
    typeof e.pos.x === "number" &&
    typeof e.pos.y === "number" &&
    typeof e.pos.z === "number";

  return {
    ...movements,
    appeals: appeals.filter(checkPos),
    incidents: incidents.filter(checkPos),
  } as { appeals: WithPos<LawsuitAppealIncident>[]; incidents: WithPos<LawsuitAppealIncident>[] };
};

const writeWithActionType = (actionType: string) => {
  if (!actionType) return "";

  return `- ${actionType}`;
};

type IWriteTitle = {
  (lawsuit: LawsuitInfo): string;
  (movement: WithPos<LawsuitAppealIncident>): string;
};

export const writeTitle: IWriteTitle = (param) => {
  if (isAppealIncident(param))
    return `${param.lawsuit.instanceNumber}º Grau ${writeWithActionType(
      param.lawsuit.lawsuitTypeName
    )}`;
  return `${param.instanceNumber}º Grau ${writeWithActionType(param.actionType)}`;
};
